import { useQueries, useQuery } from "react-query";
import { collection, doc, getDoc, getDocs, getFirestore } from "firebase/firestore";
import { IRawPropertyMember, TRawPropertyMember } from "@propely/propely-typings";
import { useMemo } from "react";

export const usePropertyMembersKey = (propertyID: string) => ["property-members", { propertyID }];
export const usePropertyMemberKey = (propertyID: string | undefined, userID: string) => ["property-members", { propertyID, userID }];

const fetchPropertyMember = async (propertyID: string | undefined, userID: string) => {
  if (!propertyID) return undefined;

  const propertyMember = await getDoc(doc(getFirestore(), "properties", propertyID, "members", userID));
  return TRawPropertyMember.parse({
    id: propertyMember.id,
    ...propertyMember.data(),
  });
};

export const usePropertyMember = (propertyID: string | undefined, userID: string) => {
  const $propertyMember = useQuery(usePropertyMemberKey(propertyID, userID), () => fetchPropertyMember(propertyID, userID), {
    enabled: !!propertyID,
  });
  return {
    $propertyMember,
    propertyMember: $propertyMember.data,
  };
};

export const usePropertyMembers = (propertyID: string) => {
  const $propertyMembers = useQuery(usePropertyMembersKey(propertyID), async () => {
    const propertyMembers = await getDocs(collection(getFirestore(), "properties", propertyID, "members"));
    const result: IRawPropertyMember[] = [];

    for (const member of propertyMembers.docs) {
      const parsedMember = TRawPropertyMember.safeParse({
        id: member.id,
        ...member.data(),
      });

      if (parsedMember.success) {
        result.push(parsedMember.data);
      }
    }
    return result;
  });

  return {
    $propertyMembers,
    propertyMembers: $propertyMembers.data,
  };
};

export const usePropertyMembersForUser = (propertyIDs: string[], userID: string) => {
  const $propertyMembers = useQueries(
    propertyIDs.map((propertyID) => ({
      queryKey: usePropertyMemberKey(propertyID, userID),
      queryFn: () => fetchPropertyMember(propertyID, userID),
    }))
  );

  const isLoading = $propertyMembers.some((result) => result.isLoading);
  const propertyMembers = useMemo(
    () =>
      $propertyMembers
        .map((result) => result.data)
        .filter((data): data is IRawPropertyMember => Boolean(data))
        .map((member, index) => ({ ...member, propertyID: propertyIDs[index] })),
    [$propertyMembers, propertyIDs]
  );
  return {
    $propertyMembers,
    propertyMembers,
    isLoading,
  };
};
